import { Box, Modal, useMediaQuery } from '@mui/material';
import ActionsMenu from 'apps/studio/studio-tab/components/actions-menu/ActionsMenu';
import SelectedImageInfo from 'apps/studio/studio-tab/components/actions-menu/selected-image-info/SelectedImageInfo';
import { selectedImageInfoStyles } from 'apps/studio/studio-tab/components/actions-menu/selected-image-info/SelectedImageInfoStyles';
import ImageInfo from 'apps/studio/studio-tab/components/image-info/ImageInfo';
import * as loadingAnimationData from 'core/assets/lottie/green-loader-animation.json';
import { ReactComponent as InfoIcon } from 'core/assets/svg/canvas-tab/settings-sidebar/info-tooltip.svg';
import { ReactComponent as Cross } from 'core/assets/svg/common/cross.svg';
import { ReactComponent as FeedbackIcon } from 'core/assets/svg/common/feedback.svg';
import { ReactComponent as Arrow } from 'core/assets/svg/modal-icons/arrow-left.svg';
import BaseButton from 'core/components/button/BaseButton';
import BaseImageWithLoader from 'core/components/image/components/BaseImageWithLoader';
import MediaFeedback from 'core/components/media-feedback/MediaFeedback';
import BaseTypography from 'core/components/typography/BaseTypography';
import { ApiStage } from 'core/constants/enums/ApiStages.ts';
import { FeedViews } from 'core/constants/enums/FeedViews.ts';
import { GenerationType } from 'core/constants/enums/GenerationType.ts';
import useFeatureFlagEnabledCheck from 'core/hooks/useFeatureFlag';
import {
	expandImageModalStyles,
	getImageDisplayStyles,
	getMaxContainerHeightStyles,
} from 'core/layouts/components/expand-image-modal/ExpandImageModalStyles';
import {
	DeleteImageButton,
	DownloadImageButton,
	IconButton,
	MobileDrawerButton,
	ShareLinkButton,
	TrueTouchImageButton,
	TwitterImageShareButton,
	UpscaleImageButton,
} from 'core/layouts/components/export-image-buttons/ExportImageButtons';
import LottieAnimation from 'core/layouts/components/lottie-animation/LottieAnimation';
import appTheme from 'core/layouts/ui-blocks/AppTheme';
import { ConditionalLayer } from 'core/layouts/ui-blocks/ReusableComponents';
import { reusableStyles, tabletBreakpoint } from 'core/layouts/ui-blocks/ReusableStyles';
import { TEXT_TO_IMAGE } from 'core/navigation/Routes';
import { toggleMediaFeedbackModal } from 'core/store/middleware/StudioCanvasMiddleware';
import {
	feedPromptModalDetailsSelector,
	feedPromptModalHasMultipleImagesSelector,
	feedPromptsModalTypeSelector,
	openFeedChangeAnythingModalSelector,
	openFeedPromptsModalSelector,
	showConfirmPromptScreenSelector,
	toggleFeedChangeAnythingModal,
	toggleFeedPromptsModal,
} from 'core/store/reducers/appReducer/AppSlice';
import { switchFeedPromptsModalSelectedImage } from 'core/store/reducers/appReducer/appSliceThunk';
import {
	openDeletePromptConfirmationModalSelector,
	studioFeedViewSelector,
	studioSelectedImagesSelector,
} from 'core/store/reducers/studioReducer/StudioSlice';
import { getOptimuxMediaUrlsSet } from 'core/utils/utils';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

function ImageOptions({ imageDetails, handleClose }) {
	const dispatch = useDispatch();
	return (
		<Box id="image-options" sx={reusableStyles.flexColWithGap(12)}>
			<ActionsMenu
				image={imageDetails?.media}
				handleClose={handleClose}
				imageId={imageDetails?.generationId}
				width={imageDetails?.width}
				height={imageDetails?.height}
				aspectRatio={imageDetails?.aspectRatio}
			/>
			<SelectedImageInfo
				imageInfo={imageDetails}
				isShown={
					!imageDetails?.isTrueTouch &&
					!imageDetails?.isChangeIt &&
					!imageDetails?.isUpscale &&
					!imageDetails?.isFaceReplace &&
					!imageDetails?.isClothSwap &&
					!(imageDetails?.generationType === GenerationType.OBJECT_PLACEMENT)
				}
			/>
			<Box
				id="give-feedback"
				sx={[
					selectedImageInfoStyles.selectedImageInfoCont,
					{ cursor: 'pointer', flexDirection: 'row' },
				]}
				onClick={(e) => {
					e.stopPropagation();
					dispatch(toggleMediaFeedbackModal(true, TEXT_TO_IMAGE));
				}}
			>
				<FeedbackIcon />
				<BaseTypography variant="b2_regular" styles={{ color: 'colors.light3' }}>
					Give Feedback
				</BaseTypography>
			</Box>
		</Box>
	);
}

function ExpandImageModal() {
	const dispatch = useDispatch();

	const isMobileScreen = useMediaQuery(appTheme.breakpoints.down('md'));
	const isTextToImageModalOpen = useSelector(openFeedPromptsModalSelector);
	const isImageModalOpen = useSelector(openFeedChangeAnythingModalSelector);
	const feedPromptsModalType = useSelector(feedPromptsModalTypeSelector);
	const imageDetails = useSelector(feedPromptModalDetailsSelector);

	const open =
		(isTextToImageModalOpen || isImageModalOpen) && feedPromptsModalType === TEXT_TO_IMAGE;
	const multipleImages = useSelector(feedPromptModalHasMultipleImagesSelector);
	const feedViewType = useSelector(studioFeedViewSelector);
	const isNotGridView = feedViewType !== FeedViews.GRID;

	const openDeletePromptConfirmationModal = useSelector(openDeletePromptConfirmationModalSelector);
	const showConfirmPromptScreen = useSelector(showConfirmPromptScreenSelector);

	const [isInfoShown, setIsInfoShown] = useState(false);
	const imageInfoRef = useRef(null);
	const selectedImages = useSelector(studioSelectedImagesSelector);
	const multiSelectMode = selectedImages.length > 0;

	const [actionsMenuDrawerOpen, setActionsMenuDrawerOpen] = useState(false);
	const toggleDrawer = (event = null) => {
		if (event && event?.type === 'keydown' && (event?.key === 'Tab' || event.key === 'Shift')) {
			return;
		}
		setActionsMenuDrawerOpen((isOpen) => !isOpen);
	};

	const handleClose = () => {
		if (isTextToImageModalOpen) {
			dispatch(toggleFeedPromptsModal({ openModal: false }));
		} else if (isImageModalOpen) {
			dispatch(toggleFeedChangeAnythingModal({ openModal: false }));
		}
		if (isInfoShown) setIsInfoShown(false);
		if (actionsMenuDrawerOpen) toggleDrawer();
	};

	const enableArrowButtons = useMemo(
		() =>
			multipleImages &&
			!showConfirmPromptScreen &&
			!openDeletePromptConfirmationModal &&
			isNotGridView &&
			feedPromptsModalType === TEXT_TO_IMAGE,
		[
			multipleImages,
			showConfirmPromptScreen,
			openDeletePromptConfirmationModal,
			isNotGridView,
			feedPromptsModalType,
		],
	);

	const leftArrowClick = useCallback(
		(e) => {
			e.stopPropagation();
			if (enableArrowButtons) {
				dispatch(switchFeedPromptsModalSelectedImage(-1));
				if (imageInfoRef.current) imageInfoRef.current.reset();
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		},
		[dispatch, enableArrowButtons],
	);

	const rightArrowClick = useCallback(
		(e) => {
			e.stopPropagation();
			if (enableArrowButtons) {
				dispatch(switchFeedPromptsModalSelectedImage());
				if (imageInfoRef.current) imageInfoRef.current.reset();
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		},
		[dispatch, enableArrowButtons],
	);
	const keyDownHandler = (e) => {
		if (e.key === 'ArrowLeft') {
			e.preventDefault();
			e.stopPropagation();
			leftArrowClick(e);
		}
		if (e.key === 'ArrowRight') {
			e.preventDefault();
			e.stopPropagation();
			rightArrowClick(e);
		}
	};

	useEffect(() => {
		if (open) {
			document.addEventListener('keydown', keyDownHandler);
		}
		return () => {
			document.removeEventListener('keydown', keyDownHandler);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [enableArrowButtons, open]);

	const isImageSuccess =
		imageDetails?.media && !imageDetails?.loading && imageDetails?.status === ApiStage.SUCCESS;
	const isImageLoading = imageDetails?.loading;
	const isButtonUpsized = true;
	const isOptimuxUrlEnabled = useFeatureFlagEnabledCheck('optimux-url', 'test');

	const { imageSet } = useMemo(
		() => getOptimuxMediaUrlsSet(imageDetails?.thumbnail, [600], isOptimuxUrlEnabled),
		[isOptimuxUrlEnabled, imageDetails?.thumbnail],
	);

	return (
		<Modal
			open={open}
			onClose={handleClose}
			aria-labelledby="expand-image-modal"
			aria-describedby="expand image"
			sx={expandImageModalStyles.modal}
		>
			<Box id="modal-wrapper" sx={expandImageModalStyles.modalWrapper} onClick={handleClose}>
				<Box id="modal-body" sx={expandImageModalStyles.modalBody}>
					<BaseButton
						id="cross-button"
						onClick={(e) => {
							e.stopPropagation();
							handleClose();
						}}
						styles={[expandImageModalStyles.crossButton]}
					>
						<Cross id="cross-icon" />
					</BaseButton>

					{multipleImages && isNotGridView && !multiSelectMode && (
						<Box
							id="arrow-left"
							sx={[
								expandImageModalStyles.arrowButton,
								{ left: { xs: '16px', [tabletBreakpoint]: 'unset' } },
							]}
						>
							<Arrow id="arrow-left" onClick={leftArrowClick} />
						</Box>
					)}

					<Box
						id="content"
						sx={[
							expandImageModalStyles.content,
							{ gap: '16px' },
							!isInfoShown && {
								'&': getMaxContainerHeightStyles(imageDetails?.height, imageDetails?.width),
							},
							!isInfoShown && {
								overflow: {
									xs: 'hidden',
									[tabletBreakpoint]: 'hidden',
								},
							},
							isInfoShown && {
								overflow: {
									xs: 'auto',
									[tabletBreakpoint]: 'hidden',
								},
								...expandImageModalStyles.contentHeightInfoShown,
							},
						]}
					>
						<ConditionalLayer isShown={!isInfoShown}>
							<Box
								id="image"
								sx={[
									expandImageModalStyles.image,
									isImageSuccess && { backgroundColor: 'colors.dark6' },
									{ '& img': { objectPosition: 'center' } },
									{
										'& img': getImageDisplayStyles(imageDetails.height, imageDetails.width),
									},
									reusableStyles.flexCol,
									{ justifyContent: 'center' },
								]}
								onClick={(e) => e.stopPropagation()}
							>
								{isImageLoading ? (
									<Box sx={[reusableStyles.flexCol, { height: '100%', width: '100%' }]}>
										<LottieAnimation animationData={loadingAnimationData} loop />
									</Box>
								) : (
									<BaseImageWithLoader
										imageProps={{
											style: {
												backgroundPosition: 'center',
												backgroundSize: 'contain',
												backgroundRepeat: 'no-repeat',
												backgroundImage: imageSet,
											},
											src: imageDetails?.media,
											alt: '',
										}}
									/>
								)}

								{isImageSuccess && !multiSelectMode && (
									<>
										<Box
											sx={[
												expandImageModalStyles.buttonsCont,
												expandImageModalStyles.topButtonsCont,
											]}
										>
											<DownloadImageButton
												upSize={isButtonUpsized}
												outputImage={imageDetails?.media}
											/>

											<Box
												sx={[
													reusableStyles.flexRowWithGap(4),
													{
														flexWrap: 'wrap',
														flex: 1,
													},
												]}
											>
												<TrueTouchImageButton
													upSize={isButtonUpsized}
													outputImage={imageDetails?.media}
													onClick={handleClose}
													isExpanded={isButtonUpsized}
												/>
												<UpscaleImageButton
													upSize={isButtonUpsized}
													outputImage={imageDetails?.media}
													onClick={handleClose}
													isExpanded={isButtonUpsized}
												/>
											</Box>
										</Box>
										<Box
											sx={[
												expandImageModalStyles.buttonsCont,
												expandImageModalStyles.exportButtonsCont,
											]}
										>
											<IconButton
												upSize={isButtonUpsized}
												icon={<InfoIcon />}
												onClick={() => setIsInfoShown(true)}
											/>
											<ShareLinkButton
												upSize={isButtonUpsized}
												outputImage={imageDetails?.media}
												mediaType={imageDetails?.mediaType}
											/>
											<TwitterImageShareButton
												upSize={isButtonUpsized}
												outputImage={imageDetails?.media}
											/>
											<DeleteImageButton
												upSize={isButtonUpsized}
												imageGenerationId={imageDetails?.generationId}
												imageBatchId={imageDetails?.batchId}
											/>
										</Box>
									</>
								)}
							</Box>
						</ConditionalLayer>
						<ConditionalLayer isShown={isInfoShown}>
							<ImageInfo
								ref={imageInfoRef}
								isImgToImg={isImageModalOpen}
								handleClose={handleClose}
								onClickExpandImage={() => setIsInfoShown(false)}
							/>
						</ConditionalLayer>
						<ConditionalLayer isShown={!isInfoShown && isImageSuccess && !multiSelectMode}>
							<Box
								sx={[
									{ '&': getMaxContainerHeightStyles(imageDetails?.height, imageDetails?.width) },
									expandImageModalStyles.actionsMenuCont,
								]}
							>
								{!isMobileScreen ? (
									<ImageOptions imageDetails={imageDetails} handleClose={handleClose} />
								) : (
									<MobileDrawerButton
										isOpen={actionsMenuDrawerOpen}
										toggleDrawer={toggleDrawer}
										sx={{ top: '8px' }}
										upSize={isButtonUpsized}
									>
										<ImageOptions imageDetails={imageDetails} handleClose={handleClose} />
									</MobileDrawerButton>
								)}
							</Box>
						</ConditionalLayer>
					</Box>
					{multipleImages && isNotGridView && !multiSelectMode && (
						<Box
							id="arrow-right"
							sx={[
								expandImageModalStyles.arrowButton,
								{ right: { xs: '16px', [tabletBreakpoint]: 'unset' } },
							]}
						>
							<Arrow
								id="arrow-right"
								style={{ transform: 'rotate(180deg)' }}
								onClick={rightArrowClick}
							/>
						</Box>
					)}
				</Box>
				<MediaFeedback mediaId={imageDetails?.generationId} route={TEXT_TO_IMAGE} />
			</Box>
		</Modal>
	);
}

export default ExpandImageModal;
